import React, { Component } from 'react';
import classNames from 'classnames';

import * as CustomPropTypes from 'lib/CustomPropTypes';
import BTE from 'lib/BTE';
import outerHeight from 'lib/outerHeight';
import $t from 'lib/trackingService';

import SlideshowSlide from './Slide';

$t('register', 'mbt_gallery_scroll');

class SlideshowViewport extends Component {
  childrenSlides = [];

  static propTypes = {
    content: CustomPropTypes.slideshow.isRequired,
    social: CustomPropTypes.socialMediaProfiles.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      isSlideshowActive: false,
      activeSlide: '',
      slideIndex: 0,
    };
  }

  componentDidMount() {
    this.calculatePositions();
    BTE.on('scroll', this.handleScroll);
    BTE.monitorImagesLoad();
    BTE.on('imagesLoaded', this.rePositionSlideshow);
    BTE.on('resize', this.rePositionSlideshow);
  }

  componentWillUnmount() {
    BTE.remove('scroll', this.handleScroll);
    BTE.remove('imagesLoaded', this.rePositionSlideshow);
    BTE.remove('resize', this.rePositionSlideshow);
  }

  handleScroll = (windowScroll) => {
    const { isSlideshowActive } = this.state;

    const bottomEdge = window.innerWidth >= 758
      ? this.positions.bottom // Desktop
      : this.positions.top + (outerHeight(this.container) - window.innerHeight); // Mobile
    const inView = windowScroll >= this.positions.top && windowScroll < bottomEdge;

    if (!isSlideshowActive && inView) {
      this.toggleSlideshow();
    } else if (isSlideshowActive && !inView) {
      this.toggleSlideshow();
    }

    this.determineActiveSlide(windowScroll);
  }

  rePositionSlideshow = () => {
    this.calculatePositions();
    this.handleScroll(window.pageYOffset);
  }

  activateSlide = (id, index) => {
    const { content: { totalSlides } } = this.props;
    const { slideIndex } = this.state;
    this.setState({
      activeSlide: id,
      slideIndex: index != null ? index : slideIndex,
    });
    $t('track', 'mbt_gallery_scroll', {
      prevSlideNumber: $t('mbt_gallery_scroll.slideNumber', -1),
      slideNumber: index + 1,
      adSlide: /^Ad/.test(id),
      totalSlides,
    });
  }

  toggleSlideshow = () => {
    const { isSlideshowActive } = this.state;
    this.setState({
      isSlideshowActive: !isSlideshowActive,
    });
  }

  pushSlide(slideElement) {
    if (slideElement && this.childrenSlides.indexOf(slideElement) === -1) {
      this.childrenSlides.push(slideElement);
    }
  }

  slidesMarkup() {
    const { activeSlide, isSlideshowActive } = this.state;
    const { content: { totalSlides }, social } = this.props;
    const markupArray = this.slideshowData()
      .filter((slide) => slide.image)
      .map((slide) => (
        <SlideshowSlide
          key={slide.image.id || null}
          slide={slide}
          totalSlides={totalSlides}
          slideshowActive={isSlideshowActive}
          isActive={activeSlide === slide.image.id}
          ref={(element) => { this.pushSlide(element); }}
          social={social}
        />
      ));

    return markupArray;
  }

  slideshowData() {
    const { content: { slides, url, headline } } = this.props;

    // Add image slide identifiers and socialshare-needed properties.
    // Need an eslint exception because an object literal is returned.
    /* eslint arrow-body-style: ["error", "as-needed", { "requireReturnForObjectLiteral": true }] */
    return slides.map((slide, index) => {
      return {
        ...slide,
        isSlide: true,
        slideIndex: index,
        slideshowUrls: url,
        slideshowHeadline: headline,
      };
    });
  }

  calculatePositions() {
    const { childrenSlides, container } = this;
    const lastSlideHeight = outerHeight(childrenSlides[childrenSlides.length - 1].container);

    this.positions = {
      top: container.getBoundingClientRect().top + window.pageYOffset,
      bottom: container.getBoundingClientRect().height
        + container.getBoundingClientRect().top
        + (window.pageYOffset - lastSlideHeight),
    };

    childrenSlides.forEach((theSlide) => {
      const { pageYOffset, innerHeight } = window;
      const { container: theContainer } = theSlide;
      const top = theContainer.getBoundingClientRect().top + pageYOffset + (innerHeight * -0.5);
      // eslint-disable-next-line no-param-reassign
      theSlide.triggers = {
        top,
        bottom: top + outerHeight(theContainer),
      };
    });
  }

  determineActiveSlide(windowScroll) {
    const { activeSlide, isSlideshowActive } = this.state;

    if (!isSlideshowActive || windowScroll <= this.childrenSlides[0].triggers.top) {
      return;
    }

    const activeChild = this.childrenSlides.filter(
      (slide) => windowScroll >= slide.triggers.top && windowScroll <= slide.triggers.bottom,
    );

    if (activeChild.length > 0) {
      const { slide } = activeChild[0].props;
      const { id } = slide.image;

      if (activeSlide !== id && isSlideshowActive) {
        this.activateSlide(id, slide.slideIndex);
      }
    } else if (activeChild.length === 0 && isSlideshowActive) {
      this.toggleSlideshow(); // deactivate slideshow
    }
  }

  render() {
    const { isSlideshowActive, slideIndex } = this.state;
    const { content: { totalSlides } } = this.props;

    return (
      <div
        className={classNames(
          'slideshow-viewport',
          {
            'slideshow-viewport--active': isSlideshowActive,
          },
        )}
        ref={(container) => { this.container = container; }}
      >
        {this.slidesMarkup()}
        <div
          className={classNames(
            'slideshow-viewport__slide-count',
            'dn-print',
          )}
          data-test="slideshow-viewport__slide-count"
        >
          <span>{ slideIndex + 1 }</span>
          /
          {totalSlides}
        </div>
      </div>
    );
  }
}

export default SlideshowViewport;
